import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import styles from '../../styles/OrganizationSignup.module.css';

// List of generic/free email providers
const GENERIC_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com',
  'yandex.com', 'gmx.com', 'fastmail.com', 'tutanota.com', 'hushmail.com',
  'inbox.com', 'mail.ru', 'qq.com', '163.com', '126.com',
  'me.com', 'msn.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.co.in',
  'googlemail.com', 'rediffmail.com', 'comcast.net', 'verizon.net'
];

const OrganizationSetup = ({ initialData, onSubmit, onCancel }) => {
  const { user } = useAuth0();
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    identifier: initialData?.identifier || '',
    type: initialData?.type || 'small_business',
    teamSize: initialData?.teamSize || '1-5'
  });

  const [errors, setErrors] = useState({});
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Check if user is using a generic email domain
  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
      const emailDomain = user.email.split('@')[1]?.toLowerCase();

      if (emailDomain && GENERIC_EMAIL_DOMAINS.includes(emailDomain)) {
        setShowEmailWarning(true);
      } else {
        setShowEmailWarning(false);
      }
    }
  }, [user]);

  const organizationTypes = [
    { value: 'individual', label: 'Individual / Sole Proprietor' },
    { value: 'small_business', label: 'Small Business' },
    { value: 'enterprise', label: 'Enterprise / Corporation' },
    { value: 'government', label: 'Government Agency' },
    { value: 'educational', label: 'Educational Institution' }
  ];

  const teamSizes = [
    { value: '1-5', label: '1-5 members' },
    { value: '6-10', label: '6-10 members' },
    { value: '11-25', label: '11-25 members' },
    { value: '26-50', label: '26-50 members' },
    { value: '50+', label: '50+ members' }
  ];

  const generateIdentifier = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 30);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      identifier: prev.identifier || generateIdentifier(name)
    }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  const handleIdentifierChange = (e) => {
    const identifier = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .substring(0, 30);
    setFormData((prev) => ({ ...prev, identifier }));
    if (errors.identifier) {
      setErrors((prev) => ({ ...prev, identifier: '' }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Organization name must be at least 3 characters';
    }

    if (!formData.identifier.trim()) {
      newErrors.identifier = 'Organization identifier is required';
    } else if (formData.identifier.length < 3) {
      newErrors.identifier = 'Identifier must be at least 3 characters';
    } else if (!/^[a-z0-9-]+$/.test(formData.identifier)) {
      newErrors.identifier = 'Identifier can only contain lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles['setup-form']}>
      <div className={styles['form-section']}>
        <h2 className={styles['section-title']}>
          Organization Information
        </h2>
        <p className={styles['section-description']}>
          Tell us about your organization. You can update these details later in settings.
        </p>

        {/* Email Warning for Generic Email Providers */}
        {showEmailWarning && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Personal Email Detected</AlertTitle>
            You're currently using a personal email address (<strong>{userEmail}</strong>) to create an organization account.
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>Organization accounts work best with a company email domain (e.g., yourname@yourcompany.com)</li>
              <li>Using a corporate email helps team members identify official communications</li>
              <li>You can still proceed with your current email if preferred</li>
            </Box>
          </Alert>
        )}

        <Box className={styles['form-fields']}>
          <TextField
            fullWidth
            required
            label="Organization Name"
            value={formData.name}
            onChange={handleNameChange}
            error={Boolean(errors.name)}
            helperText={errors.name || 'The full name of your organization'}
            inputProps={{
              'aria-label': 'Organization name',
              'aria-required': 'true',
              'aria-invalid': Boolean(errors.name)
            }}
            className={styles['form-field']}
          />

          <TextField
            fullWidth
            required
            label="Organization Identifier"
            value={formData.identifier}
            onChange={handleIdentifierChange}
            error={Boolean(errors.identifier)}
            helperText={
              errors.identifier ||
              'A unique identifier for your organization (lowercase, numbers, hyphens only)'
            }
            inputProps={{
              'aria-label': 'Organization identifier',
              'aria-required': 'true',
              'aria-invalid': Boolean(errors.identifier)
            }}
            className={styles['form-field']}
          />

          <FormControl
            fullWidth
            required
            className={styles['form-field']}
          >
            <InputLabel id="organization-type-label">
              Organization Type
            </InputLabel>
            <Select
              labelId="organization-type-label"
              id="organization-type"
              value={formData.type}
              label="Organization Type"
              onChange={(e) => handleFieldChange('type', e.target.value)}
              inputProps={{
                'aria-label': 'Organization type',
                'aria-required': 'true'
              }}
            >
              {organizationTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select the type that best describes your organization
            </FormHelperText>
          </FormControl>

          <FormControl
            fullWidth
            required
            className={styles['form-field']}
          >
            <InputLabel id="team-size-label">
              Expected Team Size
            </InputLabel>
            <Select
              labelId="team-size-label"
              id="team-size"
              value={formData.teamSize}
              label="Expected Team Size"
              onChange={(e) => handleFieldChange('teamSize', e.target.value)}
              inputProps={{
                'aria-label': 'Expected team size',
                'aria-required': 'true'
              }}
            >
              {teamSizes.map((size) => (
                <MenuItem key={size.value} value={size.value}>
                  {size.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              This helps us recommend the right plan for your needs
            </FormHelperText>
          </FormControl>
        </Box>
      </div>

      <Box className={styles['form-actions']}>
        <Button
          variant="outlined"
          onClick={onCancel}
          aria-label="Cancel organization setup"
          className={styles['btn-secondary']}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          aria-label="Continue to plan selection"
          className={styles['btn-primary']}
        >
          Continue to Plan Selection
        </Button>
      </Box>
    </form>
  );
};

export default OrganizationSetup;
